// TransferCard — Process 4. Transferable balances, transfer rules + trading
// windows, and recent transfer history. Built on ProcessCard.
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../utils/format';
import { ProcessCard } from '../../reusable';
import { ArrowRightIcon, InfoIcon } from '../../icons/Icons';
import { SwapIcon } from './manage-icons';
import { BalanceBars } from './BalanceBars';

export function TransferCard({ transfer, onAction, open, onToggle }) {
  const { t } = useTranslation();
  const total = transfer.balances.reduce((sum, b) => sum + b.balance, 0);
  const history = transfer.history || [];
  const last = history[0];

  return (
    <ProcessCard
      open={open}
      onToggle={onToggle}
      className="ma-transfer"
      tone="orange"
      icon={<SwapIcon />}
      title={t('manage.transfer.title')}
      question={t('manage.transfer.question')}
      ctaTone="blue"
      ctaLabel={t('manage.transfer.cta')}
      onCta={onAction}
      summary={last ? `${last.date} · ${last.from} → ${last.to} · ${formatCurrency(last.amount, false)}` : t('manage.transfer.summaryNone')}
    >
      <div className="ma-transfer-grid">
        <div className="ma-panel">
          <div className="ma-panel__h">{t('manage.transfer.transferableBalances')}</div>
          <BalanceBars balances={transfer.balances} total={total} />
          <div className="ma-xfer-total">
            <span className="ma-xfer-total__lbl">{t('manage.transfer.totalTransferable')}</span>
            <span className="ma-xfer-total__val">{formatCurrency(total, false)}</span>
          </div>
        </div>

        <div className="ma-side-panel">
          <div className="ma-side__h">{t('manage.transfer.transferRules')}</div>
          <dl className="ma-rules">
            <div>
              <dt>{t('manage.transfer.typeOptions')}</dt>
              <dd>{transfer.rules.types}</dd>
            </div>
            <div>
              <dt>{t('manage.transfer.minTransfer')}</dt>
              <dd>{transfer.rules.minPct}%</dd>
            </div>
            <div>
              <dt>{t('manage.transfer.increment')}</dt>
              <dd>{transfer.rules.minIncrement}%</dd>
            </div>
          </dl>

          <div className="ma-cutoffs">
            <div className="ma-cutoffs__h">
              <InfoIcon width={14} height={14} /> {t('manage.transfer.tradingWindows')}
            </div>
            <ul>
              {transfer.cutoffs.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>

          <div className="ma-side__h ma-side__h--gap">{t('manage.transfer.recentTransfers')}</div>
          {history.length ? (
            <ul className="ma-history">
              {history.map((h) => (
                <li key={`${h.date}-${h.from}-${h.to}`}>
                  <span className="ma-hist-date">{h.date}</span>
                  <span className="ma-hist-arrow">
                    <strong>{h.from}</strong>
                    <ArrowRightIcon width={13} height={13} />
                    <strong>{h.to}</strong>
                  </span>
                  <span className="ma-hist-amt">{formatCurrency(h.amount, false)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ma-history__empty">{t('manage.transfer.noTransfers')}</p>
          )}
        </div>
      </div>
    </ProcessCard>
  );
}

TransferCard.propTypes = {
  transfer: PropTypes.object.isRequired,
  onAction: PropTypes.func,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default TransferCard;
